import { useInfiniteQuery, useQuery } from "react-query";
import { ApiError, api } from "utils/api";
import { SearchRequest, SearchResponse, SearchList } from "../types";

const searchKeys = {
  base: "search",
  default: (params: SearchRequest) => [searchKeys.base, params],
  infinite: (params: SearchRequest) => [searchKeys.base, "infinite", params],
  incremental: (params: SearchRequest) => [
    searchKeys.base,
    "incremental",
    params,
  ],
  list: (list: SearchList) => [searchKeys.base, "list", list],
};

// SORT - Pass an array of field names to sort. Add "desc" after to sort descending
// sort: ['mintStatus', 'mintDate desc']

const postSearch = (request?: SearchRequest): Promise<SearchResponse> => {
  if (!request) {
    return Promise.resolve({ searchAfter: "", data: [] });
  }
  const options = {
    method: "POST",
    body: JSON.stringify({
      perPage: 5,
      ...request,
      type: request.type ? [request.type] : undefined,
    }),
  };
  return api("/projects", options);
};

/**
 * Custom hook to search for Reviewables and manage state
 */
const useSearch = (params: SearchRequest) =>
  useQuery(searchKeys.default(params), () => postSearch(params));

/**
 * Custom hook to search for Reviewables with infinite pagination and manage state
 */
const useInfiniteSearch = (params: SearchRequest) =>
  useInfiniteQuery<SearchResponse, ApiError>(
    searchKeys.infinite(params),
    ({ pageParam }) => postSearch({ searchAfter: pageParam, ...params }),
    {
      getNextPageParam: (lastPage: SearchResponse) => lastPage.searchAfter,
    }
  );

/**
 * Custom hook to search for Reviewable by name incrementally and manage state
 *
 * NOTE: If `name` is user input, make sure to debounce value with `useDebouncer()` util hook
 */
const useIncrementalSearch = (name: string, perPage = 4) =>
  useQuery<SearchResponse, ApiError, SearchResponse["data"]>(
    searchKeys.incremental({ name, perPage }),
    () => postSearch({ incremental: true, name, perPage }),
    {
      enabled: !!name,
      select: (res) => res.data,
      keepPreviousData: true,
    }
  );

const useGetMintStatusReport = () =>
  useQuery<SearchResponse, ApiError>(["project", "mint-status-report"], () =>
    postSearch({
      mintStatus: ["Not yet minted", "currently minting"],
      sort: ["mintDate"],
      perPage: 100,
    })
  );

export {
  searchKeys,
  useGetMintStatusReport,
  useIncrementalSearch,
  useInfiniteSearch,
  useSearch,
};
